/**
 * External API wire contract for the Azure `user-store` backend (see `ENDPOINTS.AzUserStore`).
 * Every route is per-user — the backend derives the user from the auth token, so no user id is
 * ever sent on the wire. `UserStoreApiService` is the only place that imports these types; the
 * slice's own state (`user-store.state.ts`) works with the naked mirrored value only, and
 * `user-store.converters.ts` is the boundary between the two.
 */

/** One stored document. `id` IS the store key — there is no separate identifier. */
export interface UserStoreEntry {
    id: string;
    gid: string;
    data: unknown;
    createdAt: string;
    modifiedAt: string;
}

/** `GET /api/v1/user-store` response — the user's keys, without their values. */
export interface UserStoreKeysResponse {
    data: string[];
}

/**
 * The most keys one bulk request (`GET /api/v1/user-store?keys=...` or `PUT /api/v1/user-store`)
 * may name — the backend's `USER_STORE_BULK_MAX_KEYS` (`UserStore.schema.ts`). The client splits
 * a longer list into several requests.
 */
export const USER_STORE_BULK_MAX_KEYS = 100;

/**
 * `GET /api/v1/user-store?values=true` and `GET /api/v1/user-store?keys=a,b` response — full
 * documents: every entry, or just the named keys that exist.
 */
export interface UserStoreEntriesResponse {
    data: UserStoreEntry[];
}

/** Response envelope shared by `GET/PUT/PATCH /api/v1/user-store/{key}` — a single entry. */
export interface UserStoreEntryResponse {
    data: UserStoreEntry;
}

/** `DELETE /api/v1/user-store/{key}` response. */
export interface UserStoreDeleteResponse {
    message: string;
}

/** `PUT /api/v1/user-store/{key}` request body — replaces the entry's `data` in full. */
export interface UserStoreWriteRequest {
    data: unknown;
}

/** One entry of a bulk write. */
export interface UserStoreBulkWriteEntry {
    key: string;
    data: unknown;
}

/**
 * `PUT /api/v1/user-store` request body — replaces each entry's `data` in full, like the
 * single-key PUT. Each key at most once. Answers with {@link UserStoreEntriesResponse}.
 */
export interface UserStoreBulkWriteRequest {
    entries: UserStoreBulkWriteEntry[];
}

/**
 * `PATCH /api/v1/user-store/{key}` request body — shallow-merged into the entry's existing
 * `data`, which must already be an object (see `UserStoreApiService.patchEntry`).
 */
export interface UserStorePatchRequest {
    data: Record<string, unknown>;
}
